import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { LogOut, MessageSquare, Pencil } from "lucide-react";
import AvatarPicker from "./AvatarPicker";
import fanApi from "@/lib/fanApi";
import { useFanAuth } from "@/context/FanAuthContext";
import { FunnyLoader } from "@/components/FunnyLoader";
import CommentComposer from "./CommentComposer";
import CommentItem from "./CommentItem";
import { Avatar } from "./CommentMeta";

const SORTS = [["popular", "Populares"], ["recent", "Recientes"]];

const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
);

export default function CommentsSection({ videoId, accentColor = "#E91E8C" }) {
  const { user, loading: authLoading, login, logout } = useFanAuth();
  const [sort, setSort] = useState("popular");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await fanApi.get("/comments", { params: { video_id: videoId, sort, page: p } });
      setItems((prev) => (p === 0 ? res.data.items : [...prev, ...res.data.items]));
      setTotal(res.data.total);
      setHasMore(res.data.has_more);
      setPage(p);
    } finally {
      setLoading(false);
    }
  }, [videoId, sort]);

  useEffect(() => {
    if (!authLoading) load(0);
  }, [load, authLoading, user?.user_id]);

  const post = async (content, parentId = null) => {
    try {
      const res = await fanApi.post("/comments", { video_id: videoId, content, parent_id: parentId });
      if (parentId) {
        setItems((prev) => prev.map((c) => (c.id === parentId ? { ...c, replies: [...c.replies, res.data] } : c)));
      } else {
        setItems((prev) => [res.data, ...prev]);
      }
      setTotal((t) => t + 1);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo publicar");
      return false;
    }
  };

  const update = (updated) =>
    setItems((prev) => prev.map((c) =>
      c.id === updated.id ? { ...c, ...updated } : { ...c, replies: c.replies.map((r) => (r.id === updated.id ? updated : r)) }
    ));

  const remove = (id) => {
    const top = items.find((c) => c.id === id);
    setTotal((t) => t - 1 - (top ? top.replies.length : 0));
    setItems((prev) => prev.filter((c) => c.id !== id).map((c) => ({ ...c, replies: c.replies.filter((r) => r.id !== id) })));
  };

  return (
    <div data-testid="comments-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h3 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}>
          <MessageSquare className="w-5 h-5" style={{ color: accentColor }} />
          COMENTARIOS <span className="text-gray-500 text-base" data-testid="comments-total">({total})</span>
        </h3>
        <div className="flex bg-white/5 rounded-full p-1">
          {SORTS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSort(key)}
              className={`text-xs font-bold px-3 py-1 rounded-full transition-colors ${sort === key ? "text-black" : "text-gray-400 hover:text-white"}`}
              style={sort === key ? { backgroundColor: accentColor } : undefined}
              data-testid={`comments-sort-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {user ? (
        <div className="mb-6">
          {user.banned ? (
            <p className="text-sm text-red-400" data-testid="comments-banned">Tu cuenta no puede comentar.</p>
          ) : (
            <div className="flex gap-3">
              <button onClick={() => setPickerOpen(true)} className="relative group h-fit" title="Cambiar avatar" data-testid="change-avatar-btn">
                <Avatar author={user} />
                <span className="absolute -bottom-1 -right-1 bg-white text-black rounded-full p-0.5 opacity-80 group-hover:opacity-100"><Pencil className="w-3 h-3" /></span>
              </button>
              <CommentComposer onSubmit={(c) => post(c)} accent={accentColor} />
            </div>
          )}
          <AvatarPicker open={pickerOpen} onOpenChange={setPickerOpen} onChanged={() => load(0)} />
          <button onClick={logout} className="mt-2 text-[11px] text-gray-500 hover:text-white flex items-center gap-1" data-testid="fan-logout-btn">
            <LogOut className="w-3 h-3" /> Cerrar sesión ({user.name})
          </button>
        </div>
      ) : (
        <button
          onClick={login}
          className="mb-6 w-full flex items-center justify-center gap-2 bg-white text-black font-bold text-sm py-3 rounded-xl hover:bg-gray-200 transition-colors"
          data-testid="comments-login-btn"
        >
          <GoogleIcon /> Inicia sesión con Google para comentar
        </button>
      )}

      {loading && page === 0 ? (
        <FunnyLoader className="py-8" />
      ) : items.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-6" data-testid="comments-empty">Sé el primero en comentar.</p>
      ) : (
        <div className="space-y-6">
          {items.map((c) => (
            <div key={c.id}>
              <CommentItem comment={c} accent={accentColor} onReply={(content) => post(content, c.id)} onChange={update} onDelete={remove} />
              {c.replies.length > 0 && (
                <div className="ml-12 mt-4 space-y-4 border-l-2 border-white/10 pl-4">
                  {c.replies.map((r) => (
                    <CommentItem key={r.id} comment={r} accent={accentColor} onChange={update} onDelete={remove} isReply />
                  ))}
                </div>
              )}
            </div>
          ))}
          {hasMore && (
            <button onClick={() => load(page + 1)} disabled={loading} className="w-full text-sm text-gray-400 hover:text-white py-2" data-testid="comments-load-more">
              {loading ? "Cargando..." : "Ver más comentarios"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
